import { useEffect, useState } from 'react';
import useCountDown from "@core/helpers/client/hooks/useCountDown";
import PriceComponent from "@/components/ui/Price";
import { toast_configs } from "@core/constants/toast_configs";
import { show_mutation_error_toast } from "@/helpers/mutationErrors";
import MediaComponent from "@core/helpers/client/components/media-component";
import Counter from '@/components/Counter';
import Button from "@/components/ui/button";
import OfferLabels from '@/components/OfferLabels';
import NotifyProduct from '@/components/NotifyProduct';
import { ProductLabelGuarantee } from './_productLabel';
import Gateway from './_gateway';
import { BrandLabel } from './_brandLabel';
import PresaleLeadTime from '@/components/PresaleLeadTime';

const AddToCartDesktop = ({
    data,
    showDetails = true,
    selected_mix_variant,
    check_product_availability,
    check_product_offer,
    add_to_cart_handler,
    add_to_cart_loading,
    item_count_in_cart,
    mix_variant_limit_min,
    mix_variant_limit_max,
    change_count_items_in_cart_handler,
    remove_from_cart_handler,
    remove_from_cart_loading,
    add_product_to_notifications_handler,
    is_notify,
    noneOrginal
}) => {

    const is_available = check_product_availability(selected_mix_variant);
    const has_offer = check_product_offer(selected_mix_variant);
    const offerLabel = selected_mix_variant?.custom_fields?.custom_offer_label;
    const discountPercent = selected_mix_variant?.price?.discount_percent;
    const expire_at = parseInt(selected_mix_variant?.price?.offer_expireAt);
    const start_at = parseInt(selected_mix_variant?.price?.offer_startAt);

    // زمان فقط بعد از mount خوانده می‌شود: Date.now() در رندر سرور و hydration فرق می‌کند و متن شمارنده
    // hydration mismatch می‌ساخت. تا آن موقع شمارنده و نوار «فروش رفته» نمایش داده نمی‌شوند.
    const [now, setNow] = useState(null);

    useEffect(() => {
        const shouldTick =
            selected_mix_variant?.price?.show_discount_timer &&
            has_offer &&
            expire_at > 0 &&
            start_at > 0 &&
            is_available;

        if (!shouldTick) return;

        setNow(Date.now());

        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, [selected_mix_variant, has_offer, is_available]);

    const is_offer_active =
        selected_mix_variant?.price?.show_discount_timer &&
        has_offer &&
        expire_at > 0 &&
        start_at > 0 &&
        is_available &&
        now !== null &&
        expire_at > now;

    const timer = is_offer_active ? expire_at - now : undefined;

    const total_time = expire_at - start_at;
    const remain_time = timer;

    const timer_percent = total_time > 0 && remain_time > 0
        ? (1 - remain_time / total_time) * 100
        : 0;

    const { counter_down, convert_countdown_time } = useCountDown({
        number: timer ? Math.floor(timer / 1000) : 0,
        countdown_className:
            "dir-ltr whitespace-nowrap h-[18px] lg:h-[22px] text-sm font-bold text-jmt-red-700",
    })


    const removeHandler = (e) => {
        remove_from_cart_handler(e, {
            succes_callback: () => { },
            error_callback: show_mutation_error_toast,
        });
    };

    const changeCountHandler = (value) => {
        change_count_items_in_cart_handler({
            count: value,
            succes_callback: () => { },
            error_callback: show_mutation_error_toast,
        });
    };

    const addToCart = async (e) => {
        await add_to_cart_handler(e, {
            count: mix_variant_limit_min() || 1,
            // افزودن موفق دیگر توست نمی‌دهد؛ تبدیل شدن دکمه به شمارنده خودش
            // بازخورد کافی است. فقط خطا همچنان اطلاع‌رسانی می‌شود.
            success_callback: () => { },
            error_callback: () =>
                toast_configs("خطا در افزودن به سبد خرید", "error", {
                    className: "custome_toastify",
                    position: "bottom-center",
                    autoClose: 2000,
                }),
        });
    };

    return (
        <div className="bg-jmt-neutral-50 border border-jmt-neutral-200 rounded-md overflow-hidden">

            <div className="w-full h-1.5 bg-jmt-black"></div>

            <div className="px-3 py-4">

                {/* offer labels & timer */}
                {is_available ?
                    <>
                        {offerLabel || timer ?
                            <div className="flex items-center justify-between pb-4">
                                <OfferLabels offerLabel={offerLabel} size="lg" />
                                <div className="flex items-center">
                                    {timer > 0 && (
                                        <>
                                            {convert_countdown_time(counter_down, {
                                                formats: ["hours", "minutes", "seconds"],
                                                convertDaysToHours: true
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                            : ""}
                    </> : ""}

                {/* img & variants products */}
                {is_available && showDetails ?
                    <div className="flex gap-1 pb-4">
                        <div className="w-[82px] h-[82px] shrink-0">
                            <MediaComponent
                                media={selected_mix_variant?.media || selected_mix_variant?.media_gallery?.[0]}
                                width={100}
                                height={100}
                                style={{ width: "100%", height: "100%" }}
                                className="object-contain"
                            />
                        </div>

                        <div className="flex-1">

                            {/* brand */}
                            <div className="flex items-center gap-1 mb-2">
                                <span className="text-xs font-bold text-jmt-main"> {data?.brand?.title || 'برند نامشخص'} </span>

                                {/* brand desktop */}
                                <BrandLabel brand={data?.brand} />

                            </div>

                            <h3 className="text-xs text-jmt-main mb-2">
                                {data?.title}
                            </h3>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                    <div
                                        className="w-3.5 h-3.5 rounded-full border border-jmt-neutral-200 overflow-hidden flex items-center justify-center"
                                        style={{
                                            backgroundColor: selected_mix_variant?.labels_obj?.colors?.value?.has_cover ? 'transparent' : (selected_mix_variant?.labels_obj?.colors?.value?.color || '#ccc')
                                        }}>
                                        {selected_mix_variant?.labels_obj?.colors?.value?.has_cover && selected_mix_variant?.labels_obj?.colors?.cover?.url ? (
                                            <img
                                                src={color.value.cover.url}
                                                alt={color.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : null}
                                    </div>

                                    <span className='text-xs text-jmt-main'>{selected_mix_variant?.labels_obj?.colors?.title}</span>
                                </div>

                                <div className="border-r border-jmt-neutral-300 h-3"></div>

                                <span className="text-xs text-jmt-main">{selected_mix_variant?.labels_obj?.sizes?.title}</span>
                            </div>
                        </div>

                    </div>
                    : ""}

                {/* stock */}
                {is_available &&
                    (selected_mix_variant?.details?.count < 4 ||
                        (
                            selected_mix_variant?.details?.limit_max &&
                            selected_mix_variant?.details?.limit_max < 4
                        )
                    ) && (
                        <div className="flex items-center gap-1 text-xs font-bold text-jmt-red-700 pb-4">

                            <p className="flex items-center gap-1">

                                {
                                    selected_mix_variant?.details?.count < 4
                                        ? selected_mix_variant?.details?.count
                                        : selected_mix_variant?.details?.limit_max
                                }

                                <span>  عدد باقیمانده </span>
                            </p>
                        </div>
                    )}

                {/* price & progressbar */}
                {is_available ?
                    <div className="space-y-1 text-left">

                        {has_offer ? (
                            <>
                                <div className="flex items-center justify-end gap-1">
                                    <PriceComponent
                                        price={selected_mix_variant?.price?.price}
                                        className="text-jmt-neutral-400 line-through text-sm [&>.price-currency]:hidden" />

                                    {discountPercent && (
                                        <div className="flex items-center justify-center bg-jmt-red-700 rounded-md px-1 py-px ">
                                            <span className="text-jmt-white text-xs font-bold">
                                                {discountPercent}٪
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between gap-1">
                                    {is_offer_active ? (
                                        <span className="text-xs text-jmt-red-700">
                                            {timer_percent?.toFixed()}٪ فروش رفته
                                        </span>
                                    ) : <span />}
                                    <PriceComponent
                                        price={selected_mix_variant?.price?.main_price}
                                        className="font-bold text-lg text-neutral-900"
                                    />
                                </div>

                                {is_offer_active && (
                                    <div dir="ltr" className="h-1 bg-jmt-neutral-200 rounded-lg overflow-hidden">
                                        <div className="h-1 bg-jmt-red-700 rounded-lg transition-all duration-300"
                                            style={{ width: `${timer_percent?.toFixed()}%` }} />
                                    </div>
                                )}
                            </>
                        ) : <PriceComponent
                            price={selected_mix_variant?.price?.main_price}
                            className="font-bold text-base text-neutral-900"
                        />}


                    </div>
                    : <div className="w-full flex flex-col items-start">

                        <div className="text-jmt-neutral-800 font-bold text-sm pb-4">
                            این کالا فعلا موجود نیست، با فشردن دکمه زیر به محض موجود شدن، به شما خبر می‌دهیم.
                        </div>

                        <NotifyProduct
                            add_product_to_notifications_handler={add_product_to_notifications_handler}
                            mix_variant={selected_mix_variant}
                            is_notify={is_notify} />

                    </div>
                }


                {/* add to cart */}
                {is_available && (
                    <div className="w-full order-2 mt-4">
                        {item_count_in_cart() ? (
                            <Counter
                                min={mix_variant_limit_min()}
                                max={mix_variant_limit_max()}
                                removeHandler={removeHandler}
                                quantity={item_count_in_cart()}
                                onChange={changeCountHandler}
                                loading={add_to_cart_loading[selected_mix_variant?.keys]}
                                remove_from_cart_loading={remove_from_cart_loading[selected_mix_variant?.keys]}
                            />
                        ) : (


                            <Button
                                text={"افزودن به سبد خرید"}
                                onClick={addToCart}
                                loading={add_to_cart_loading[selected_mix_variant?.keys]}
                                color_type="primary"
                                className="whitespace-nowrap w-full" />
                        )}
                    </div>
                )}

                {is_available ?
                    <>
                        {/* ProductLabelGuarantee */}
                        <div className="space-y-2 pt-4">
                            <ProductLabelGuarantee noneOrginal={noneOrginal} />
                            {/* زمان آماده‌سازی - فقط پیش‌فروش */}
                            {selected_mix_variant?.presale?.available && (
                                <PresaleLeadTime
                                    hours={selected_mix_variant?.presale?.lead_hours}
                                    className="text-jmt-neutral-700 text-xs font-bold"
                                    textClassName="font-bold text-xs text-jmt-neutral-700"
                                    icon={
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 2H14M12 14L15 11M20 14C20 18.4183 16.4183 22 12 22C7.58172 22 4 18.4183 4 14C4 9.58172 7.58172 6 12 6C16.4183 6 20 9.58172 20 14Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    }
                                />
                            )}
                        </div>

                        {/* installment */}
                        <Gateway products={[{ price: selected_mix_variant?.price?.main_price, count: item_count_in_cart() || 1 }]} />
                    </>
                    : ""}

            </div>
        </div>
    );
};

export default AddToCartDesktop;

